'use client';

import { useAction, useAtom } from '@reatom/npm-react';
import { BACKEND_API, Product, SearchProductEntity } from '@trwl/backend.api';
import { ProductCard } from '@trwl/entities/product/card';
import { IsBot } from '@trwl/hooks';
import { PageDto } from '@trwl/shared/types';
import {
  addRecentProductAction,
  dislikeAction,
  isItemLiked,
  likeAction,
  likesAtom,
  reloadProductById,
  setDuration,
  setShow,
} from '@trwl/store';
import { Description } from '@trwl/widgets/product/description/Desctiption';
import { ProductInfo } from '@trwl/widgets/product/productInfo';
import { RelationProductsSlider } from '@trwl/widgets/product/productInfo/components/RelationProductsSlider';
import { ProductShoe } from '@trwl/widgets/product/shoe';
import { NewBalanceSizeGrid } from '@trwl/widgets/product/sizeGrid/NewBalanceSizeGrid';
import { SizeGrid } from '@trwl/widgets/product/sizeGrid/SizeGrid';
import { ProductSlider } from '@trwl/widgets/product/slider';
import { useUnit } from 'effector-react';
import { revalidatePath } from 'next/cache';
import { usePathname } from 'next/navigation';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

export interface ProductPageProps {
  slug: string;
  data: Product;
  relationProducts: PageDto<SearchProductEntity>;
  similar: SearchProductEntity[];
}

const PROPERTY_KEYS: { [key: string]: string } = {
  'series': 'Модель',
  'model': 'Модель',
  'style id': 'Артикул',
  'release date': 'Дата релиза',
} as const;

export const ProductPage: FC<ProductPageProps> = ({
  slug,
  data,
  relationProducts,
  similar,
}) => {
  const pathname = usePathname();
  const isBot = IsBot();
  const [changeShow, changeDuration] = useUnit([setShow, setDuration]);
  const reloadProduct = useUnit(reloadProductById);
  const addRecent = useAction(addRecentProductAction);
  const [product, setProduct] = useState<Product>(data);
  const [likes] = useAtom(likesAtom);
  const like = useAction(likeAction);
  const dislike = useAction(dislikeAction);

  const [likesCount, setLikesCount] = useState<number>(0);
  const sizeGridRef = useRef<HTMLDivElement>(null);

  const images = useMemo(
    () => product.images.map((image) => image.url),
    [product],
  );

  const baseProperties = useMemo(() => {
    const usedKeys = new Set<string>();
    return product.baseProperties
      .map((baseProperty) => ({
        ...baseProperty,
        basePropertyKey:
          PROPERTY_KEYS[baseProperty.basePropertyKey.toLowerCase()],
      }))
      .filter(({ basePropertyKey }) => {
        if (!basePropertyKey || usedKeys.has(basePropertyKey)) return false;
        usedKeys.add(basePropertyKey);
        return true;
      });
  }, [product.baseProperties]);

  useEffect(() => {
    if (product) addRecent(product);
  }, [product, addRecent]);

  useEffect(() => {
    changeDuration(0);
    changeShow(false);
  }, [changeShow, changeDuration]);

  useEffect(() => {
    BACKEND_API.StatsApi.getStatsByProductId(product.id).then(({ data }) => {
      setLikesCount(data.likes);
    });
  }, [setLikesCount, product]);

  useEffect(() => {
    const updateProduct = () => {
      if (!data) return;
      BACKEND_API.ProductApi.getProductById(data.id)
        .then((response) => {
          if (response.data.updatedAt !== product.updatedAt) {
            setProduct(response.data);
            reloadProduct(data.id);
            revalidatePath(pathname, 'page');
          }
        })
        .catch(console.error);
    };

    updateProduct();

    const interval = setInterval(() => {
      updateProduct();
    }, 3000);

    return () => clearInterval(interval);
  }, [data, product.updatedAt, pathname, reloadProduct]);

  useEffect(() => {
    if (isBot) return;
    BACKEND_API.ParserApi.updatePriceByProductId(data.id);
  }, [isBot, data.id]);

  const liked = useMemo(
    () => Boolean(isItemLiked(likes, product)),
    [likes, product],
  );

  const handleLike = useCallback(() => {
    if (isItemLiked(likes, product)) {
      setLikesCount((count) => Math.max(0, count - 1));
      dislike(product);
    } else {
      setLikesCount((count) => count + 1);
      like(product);
    }
  }, [likes, product, dislike, like]);

  return (
    <div className="flex flex-col items-center w-[100%] max-w-[1120px] overflow-hidden px-[20px] min-[1140px]:px-0 mx-auto">
      {product && (
        <>
          <div className="mt-4 lg:mt-8 flex flex-col lg:grid lg:grid-cols-5 w-[100%] gap-x-[50px] gap-y-[20px] content-between">
            <div className="flex flex-col items-center lg:items-start w-full col-span-5 lg:col-span-3">
              <ProductShoe
                liked={liked}
                product={product}
                handleLike={handleLike}
                isNew={Boolean(product.isNew)}
                images={images}
              />
              <div className={'hidden lg:block w-full'}>
                <RelationProductsSlider
                  relationProducts={relationProducts}
                  size={110}
                />
              </div>
            </div>
            <ProductInfo
              likesCount={likesCount}
              product={product}
              slug={slug}
              sizeGridRef={sizeGridRef}
              relationProducts={relationProducts}
            />
          </div>
          <div className={'mt-[35px] w-full'}>
            <Description baseProperties={baseProperties} />
          </div>
          <div className={'mt-[12px] w-full'}>
            <SizeGrid sizeGrid={product.sizeGrid} ref={sizeGridRef} />
          </div>
          {product.brandId === 4 && (
            <div className={'mt-[35px] w-full'}>
              <NewBalanceSizeGrid />
            </div>
          )}

          {Boolean(similar) && (
            <div className="flex flex-row w-[100%] mt-[35px]">
              <ProductSlider
                title={'Похожие товары'}
                products={similar.map((product) => (
                  <ProductCard
                    showLikes={true}
                    target={'_blank'}
                    key={product.id}
                    product={product}
                    priority={true}
                  />
                ))}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
